import React, { useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Heart, 
  Zap, 
  Shield, 
  Swords, 
  ChevronRight
} from 'lucide-react';
import { TypeBadge } from './shared';
import { getPokemonImage, createPokemonImageErrorHandler, useImageMapReady } from '../utils/pokemonImageResolver';
import { useLanguage } from '../contexts/LanguageContext';

const PokemonCard = memo(({ pokemon, index, viewMode = 'grid' }) => {
  const { language, gamePokemonName, t } = useLanguage();
  const imageMapReady = useImageMapReady(); // Triggers re-render when image map loads
  const displayName = gamePokemonName(pokemon.Pokemon_ID) || (language === 'th' ? pokemon.Pokemon_Name_TH : pokemon.Pokemon_Name_EN) || pokemon.Pokemon_Name_EN || pokemon.Pokemon_ID;
  const altName = language === 'th' ? pokemon.Pokemon_Name_EN : pokemon.Pokemon_Name_TH;
  const isShadow = pokemon.Pokemon_ID?.toLowerCase().includes('shadow') || pokemon.Pokemon_Name_EN?.toLowerCase().includes('shadow');
  const primaryName = isShadow && !displayName?.toLowerCase().includes('shadow') ? `${displayName} Shadow` : displayName;
  const secondaryName = isShadow && !altName?.toLowerCase().includes('shadow') ? `${altName} Shadow` : altName;
  // Use pokemonImageResolver - SINGLE SOURCE OF TRUTH
  const primaryImageUrl = getPokemonImage(pokemon, 'default');
  const handleImageError = createPokemonImageErrorHandler(pokemon, { variant: 'default' });

  const getTypeColor = (type) => {
    const typeColors = {
      'Fire': 'bg-red-500',
      'Water': 'bg-blue-500',
      'Grass': 'bg-green-500',
      'Electric': 'bg-yellow-500',
      'Psychic': 'bg-purple-500',
      'Ice': 'bg-cyan-500',
      'Dragon': 'bg-indigo-500',
      'Dark': 'bg-gray-800',
      'Fairy': 'bg-pink-500',
      'Fighting': 'bg-orange-500',
      'Poison': 'bg-violet-500',
      'Ground': 'bg-amber-600',
      'Flying': 'bg-sky-500',
      'Bug': 'bg-lime-500',
      'Rock': 'bg-stone-500',
      'Ghost': 'bg-purple-600',
      'Steel': 'bg-gray-500',
      'Normal': 'bg-gray-400'
    };
    return typeColors[type] || 'bg-gray-400';
  };

  const formatTypes = (typesString) => {
    if (!typesString) return [];
    return typesString.split(',').map(type => type.trim());
  };

  const types = formatTypes(pokemon.types);
  
  // Calculate total stats for list view
  const totalStats = (pokemon.HP || 0) + (pokemon.Attack || 0) + (pokemon.Defense || 0);
  
  if (viewMode === 'list') {
    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
        whileHover={{ x: 5 }}
        className="pokemon-card-list p-4 group bg-white rounded-xl shadow-pokemon hover:shadow-xl transition-all duration-300"
      >
        <div className="flex items-center gap-4">
          {/* Pokemon Image */}
          <Link to={`/pokemon/${pokemon.Pokemon_ID}`} className="cursor-pointer">
            <div 
              className={`relative w-20 h-20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 overflow-hidden flex-shrink-0 ${
                pokemon.Pokemon_ID?.toLowerCase().includes('shadow') || pokemon.Pokemon_Name_EN?.toLowerCase().includes('shadow')
                  ? ''
                  : 'bg-gradient-to-br from-gray-100 to-gray-200'
              }`}
            >
              {/* Purple Smoke Background for Shadow Pokemon */}
              {(pokemon.Pokemon_ID?.toLowerCase().includes('shadow') || pokemon.Pokemon_Name_EN?.toLowerCase().includes('shadow')) && (
                <>
                  <div 
                    className="absolute inset-0 z-0 opacity-40"
                    style={{
                      background: `radial-gradient(ellipse at center, 
                        rgba(147, 51, 234, 0.4) 0%, 
                        rgba(124, 58, 237, 0.3) 20%,
                        rgba(139, 92, 246, 0.25) 40%,
                        rgba(168, 85, 247, 0.2) 60%,
                        transparent 80%)`,
                      backgroundBlendMode: 'multiply'
                    }}
                  />
                  <div 
                    className="absolute inset-0 z-0"
                    style={{
                      background: `radial-gradient(circle at 50% 70%, 
                        rgba(147, 51, 234, 0.3) 0%, 
                        rgba(124, 58, 237, 0.2) 30%,
                        transparent 60%)`,
                      filter: 'blur(6px)',
                      transform: 'scale(1.3)'
                    }}
                  />
                </>
              )}
              <img
                src={primaryImageUrl}
                alt={`${pokemon.Pokemon_Name_EN} - ${pokemon.Pokemon_Name_TH}`}
                className={`w-full h-full object-contain p-1 relative z-10 ${
                  (pokemon.Pokemon_ID?.toLowerCase().includes('shadow') || pokemon.Pokemon_Name_EN?.toLowerCase().includes('shadow'))
                    ? 'z-10' 
                    : ''
                }`}
                onError={handleImageError}
                loading="lazy"
              />
            </div>
          </Link>
          
          {/* Pokemon Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Link to={`/pokemon/${pokemon.Pokemon_ID}`} className="cursor-pointer">
                <h3 className="text-lg font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-200 truncate">
                  {primaryName}
                </h3>
              </Link>
              {/* Only Shiny icon */}
              {pokemon.has_shiny === 1 && (
                <div className="w-5 h-5 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center" title="Shiny">
                  <img src="/images/shiny_icon.webp" alt="Shiny" className="w-3 h-3" />
                </div>
              )}
            </div>
            <Link to={`/pokemon/${pokemon.Pokemon_ID}`} className="cursor-pointer">
              <p className="text-sm text-gray-600 font-medium truncate group-hover:text-blue-600 transition-colors duration-200">
                {secondaryName}
              </p>
            </Link>
            <p className="text-xs text-gray-500 font-mono mb-2">
              #{pokemon.Pokemon_ID}
            </p>
            
            {/* Types */}
            <div className="flex flex-wrap gap-1 mb-2">
              {types.map((type, idx) => (
                <TypeBadge key={idx} type={type} size="small" />
              ))}
            </div>
          </div>
          
          {/* Stats Summary */}
          <div className="flex items-center gap-6 text-sm">
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">
                {pokemon.Max_CP_Lv50 ? pokemon.Max_CP_Lv50.toLocaleString() : 'N/A'}
              </div>
              <div className="text-xs text-gray-500">Max CP</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-600">
                {totalStats.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors duration-200" />
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
      whileHover={{ y: -5, scale: 1.02 }}
      className="pokemon-card p-6 group bg-white rounded-2xl shadow-pokemon hover:shadow-xl transition-all duration-300"
    >
      <div className="block">
        {/* Pokemon Image */}
        <Link to={`/pokemon/${pokemon.Pokemon_ID}`} className="cursor-pointer">
          <div 
            className={`relative w-full h-48 rounded-xl mb-4 flex items-center justify-center group-hover:scale-105 transition-transform duration-300 overflow-hidden ${
              pokemon.Pokemon_ID?.toLowerCase().includes('shadow') || pokemon.Pokemon_Name_EN?.toLowerCase().includes('shadow')
                ? ''
                : 'bg-gradient-to-br from-gray-100 to-gray-200'
            }`}
          >
            {/* Purple Smoke Background for Shadow Pokemon */}
            {(pokemon.Pokemon_ID?.toLowerCase().includes('shadow') || pokemon.Pokemon_Name_EN?.toLowerCase().includes('shadow')) && (
              <>
                <div 
                  className="absolute inset-0 z-0 opacity-40"
                  style={{
                    background: `radial-gradient(ellipse at center, 
                      rgba(147, 51, 234, 0.4) 0%, 
                      rgba(124, 58, 237, 0.3) 20%,
                      rgba(139, 92, 246, 0.25) 40%,
                      rgba(168, 85, 247, 0.2) 60%,
                      rgba(196, 181, 253, 0.15) 80%,
                      transparent 100%)`,
                    backgroundBlendMode: 'multiply'
                  }}
                />
                <div 
                  className="absolute inset-0 z-0"
                  style={{
                    background: `radial-gradient(circle at 50% 70%, 
                      rgba(147, 51, 234, 0.3) 0%, 
                      rgba(124, 58, 237, 0.2) 30%,
                      rgba(139, 92, 246, 0.1) 50%,
                      transparent 70%)`,
                    filter: 'blur(8px)',
                    transform: 'scale(1.2)'
                  }}
                />
              </>
            )}
            <img
              src={primaryImageUrl}
              alt={`${pokemon.Pokemon_Name_EN} - ${pokemon.Pokemon_Name_TH}`}
              className={`w-full h-full object-contain p-2 relative z-10 ${
                (pokemon.Pokemon_ID?.toLowerCase().includes('shadow') || pokemon.Pokemon_Name_EN?.toLowerCase().includes('shadow'))
                  ? 'z-10' 
                  : ''
              }`}
              onError={handleImageError}
              loading="lazy"
            />
          
          {/* Simple Badge - Only Shiny indicator */}
          {pokemon.has_shiny === 1 && (
            <div className="absolute top-2 right-2">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-md" title={t('badge.shinyTitle')}>
                <img src="/images/shiny_icon.webp" alt="Shiny" className="w-4 h-4" />
              </div>
            </div>
          )}
          </div>
        </Link>

        {/* Pokemon Info */}
        <div className="space-y-3">
          {/* Names */}
          <div className="mb-3">
            <div className="flex items-center gap-1 mb-1 flex-wrap">
              <Link to={`/pokemon/${pokemon.Pokemon_ID}`} className="cursor-pointer">
                <h3 className="text-lg font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-200">
                  {primaryName}
                </h3>
              </Link>
            </div>
            <Link to={`/pokemon/${pokemon.Pokemon_ID}`} className="cursor-pointer">
              <p className="text-sm text-gray-600 font-medium group-hover:text-blue-600 transition-colors duration-200">
                {secondaryName}
              </p>
            </Link>
            <p className="text-xs text-gray-500 font-mono">
              #{pokemon.Pokemon_ID}
            </p>
          </div>

          {/* Types */}
          <div className="flex flex-wrap gap-2 mb-4">
            {types.map((type, idx) => (
              <motion.div
                key={idx}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: idx * 0.1 }}
              >
                <TypeBadge type={type} size="small" />
              </motion.div>
            ))}
          </div>

          {/* Stats */}
          <div className="space-y-3">
            {/* Max CP - Highlighted */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-3 mb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Zap className="w-5 h-5 text-blue-500" />
                  <span className="text-sm font-medium text-gray-700">{t('stats.maxCp')}</span>
                </div>
                <span className="text-lg font-bold text-blue-600">
                  {pokemon.Max_CP_Lv50 ? pokemon.Max_CP_Lv50.toLocaleString() : 'N/A'}
                </span>
              </div>
            </div>
            
            {/* Individual Stats */}
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <Heart className="w-3 h-3 text-red-500" />
                  <span className="text-xs text-gray-600">{t('stats.hp')}</span>
                </div>
                <div className="text-sm font-bold text-gray-800">{pokemon.HP || 'N/A'}</div>
              </div>
              
              <div className="text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <Swords className="w-3 h-3 text-orange-500" />
                  <span className="text-xs text-gray-600">{t('stats.atk')}</span>
                </div>
                <div className="text-sm font-bold text-gray-800">{pokemon.Attack || 'N/A'}</div>
              </div>
              
              <div className="text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <Shield className="w-3 h-3 text-blue-500" />
                  <span className="text-xs text-gray-600">{t('stats.def')}</span>
                </div>
                <div className="text-sm font-bold text-gray-800">{pokemon.Defense || 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>
        
      </div>
    </motion.div>
  );
});

PokemonCard.displayName = 'PokemonCard';

export default PokemonCard;
